import { useMemo } from 'react'
import { useAuth } from './useAuth'
import { PermissionKey } from '../types/permissions'
import { withLegacyFallbacks } from '../access/legacyFallbacks'

// System Tenant ID (cross-tenant admin)
const SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000001'

/**
 * Hook pour gérer les permissions granulaires
 * Les permissions viennent directement de la base de données sur le user
 * Optimized with useMemo to avoid recalculations on every render
 * 
 * Retourne à la fois les nouveaux helpers ET l'objet `can` pour compatibilité
 */
export function usePermissions() {
  const { user, isLoading } = useAuth()

  return useMemo(() => {
    // Wait for user to be loaded before computing permissions
    if (isLoading || !user) {
      return {
        user: null,
        isLoading: isLoading || !user,
        isAdmin: false,
        isAgent: false,
        isUser: false,
        isSystemAdmin: false,
        hasPermission: (_permission: PermissionKey) => false,
        hasAnyPermission: (..._permissions: PermissionKey[]) => false,
        hasAllPermissions: (..._permissions: PermissionKey[]) => false,
        // Objet can pour compatibilité avec ancien code
        can: {
          accessDialer: false,
          getNextCall: false,
          setCallOutcome: false,
          viewDialerStats: false,
          pauseQueue: false,
          resumeQueue: false,
          viewAuditLogs: false,
          manageTenants: false,
          viewAllTenants: false,
          viewAllUsers: false,
          viewAllCampaigns: false,
          viewAllContacts: false,
          manageTeam: false,
          cleanupTokens: false,
          viewTokenStats: false,
          manageContacts: false,
          manageCampaigns: false,
          manageCalls: false,
          viewRecordings: false,
          importData: false,
          canAccessImport: false,
        }
      }
    }

    const isAdmin = user?.role === 'ADMIN'
    const isAgent = user?.role === 'AGENT'
    const isUserRole = user?.role === 'USER'
    const isSystemAdmin = isAdmin && user?.tenantId === SYSTEM_TENANT_ID

    // L-NAV-9 : le lecteur de drapeaux applique (1) la compat descendante des
    // nouveaux drapeaux d'écran (`access/legacyFallbacks`) et (2) le passe-
    // droit du compte système — tout, sans dépendre d'un drapeau, comme
    // `PermissionsGuard` côté serveur.
    const hasRawPermission = (permission: PermissionKey): boolean => Boolean((user as any)[permission])
    const hasEffectivePermission = withLegacyFallbacks(hasRawPermission)
    const hasPermission = (permission: PermissionKey): boolean => isSystemAdmin || hasEffectivePermission(permission)

    const hasAnyPermission = (...permissions: PermissionKey[]): boolean => {
      return permissions.some(permission => hasPermission(permission))
    }

    const hasAllPermissions = (...permissions: PermissionKey[]): boolean => {
      return permissions.every(permission => hasPermission(permission))
    }

    // Objet can pour compatibilité avec ancien code (Sidebar, etc.)
    // Map les anciennes propriétés vers les nouvelles permissions
    const can = {
      accessDialer: hasPermission('canAccessCalls'),
      getNextCall: hasPermission('canMakeOutbound') || hasPermission('canReceiveInbound'),
      setCallOutcome: hasPermission('canMakeOutbound') || hasPermission('canReceiveInbound'),
      viewDialerStats: hasPermission('canAccessStats'),
      pauseQueue: isAdmin,
      resumeQueue: isAdmin,
      viewAuditLogs: hasPermission('canViewSystemLogs'),
      manageTenants: hasPermission('canEditTenants') || hasPermission('canCreateTenants'),
      viewAllTenants: hasPermission('canViewAllTenants'),
      viewAllUsers: isSystemAdmin,
      viewAllCampaigns: isSystemAdmin,
      viewAllContacts: isSystemAdmin,
      manageTeam: hasPermission('canViewUsers'),
      cleanupTokens: isAdmin,
      viewTokenStats: isAdmin,
      manageContacts: hasPermission('canEditContacts') || hasPermission('canCreateContacts'),
      manageCampaigns: hasPermission('canEditCampaigns') || hasPermission('canCreateCampaigns'),
      manageCalls: hasPermission('canAccessCalls'),
      viewRecordings: hasPermission('canAccessCalls'),
      importData: hasPermission('canImportContacts'),
      canAccessImport: hasPermission('canAccessImport'),
    }

    return {
      user,
      isLoading: false,
      isAdmin,
      isAgent,
      isUser: isUserRole,
      isSystemAdmin,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      can,
    }
  }, [user, isLoading]) // Only recalculate when user or isLoading changes
}
